import React, { createContext, useContext, useState, useCallback } from 'react';
import { X, Bell, Info, AlertCircle, CheckCircle } from 'lucide-react';

const ToastContext = createContext();

export const useToast = () => useContext(ToastContext);

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const addToast = useCallback((message, type = 'info', title = '') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type, title }]);
        setTimeout(() => {
            removeToast(id);
        }, 5000);
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ addToast, removeToast }}>
            {children}
            <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-3 w-full max-w-sm pointer-events-none">
                {toasts.map(toast => (
                    <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
                ))}
            </div>
        </ToastContext.Provider>
    );
};

const ToastItem = ({ toast, onRemove }) => {
    const [isVisible, setIsVisible] = useState(true);

    const handleRemove = () => {
        setIsVisible(false);
        setTimeout(() => onRemove(toast.id), 300);
    };

    const icons = {
        info: <Info className="w-5 h-5 text-blue-500" />,
        success: <CheckCircle className="w-5 h-5 text-green-500" />,
        error: <AlertCircle className="w-5 h-5 text-red-500" />,
        chat: <Bell className="w-5 h-5 text-indigo-500" />
    };

    return (
        <div 
            className={`
                pointer-events-auto bg-white/95 backdrop-blur-md border border-gray-100 
                shadow-xl rounded-2xl p-4 flex gap-4 items-start
                transition-all duration-300 transform
                ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
            `}
        >
            <div className={`p-2 rounded-xl flex-shrink-0 ${
                toast.type === 'chat' ? 'bg-indigo-50' : 
                toast.type === 'success' ? 'bg-green-50' :
                toast.type === 'error' ? 'bg-red-50' : 'bg-blue-50'
            }`}>
                {icons[toast.type] || icons.info}
            </div>
            
            <div className="flex-1 min-w-0">
                {toast.title && (
                    <h4 className="text-sm font-bold text-gray-900 truncate mb-0.5">
                        {toast.title}
                    </h4>
                )}
                <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
                    {toast.message}
                </p>
            </div>
            
            <button 
                onClick={handleRemove}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors text-gray-400"
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
};
